import React, { useEffect, useState, useContext } from 'react';
import { Form, Input, Button, Spin, Space } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Modal from '../../../main/components/Modal';
import ActionButton from '../../../main/components/Button';
import { Divider } from '../../components/Divider';
import SelectUser from '../Selects/SelectUser';
import { CellFullName } from '../../components/UserList';
import { shareClassroomLink } from '../../support/serverRoutes';
import copyToClipboard from '../../../main/utils/helpers/copyToClipboard';
import ModalDeleteConfirmation from '../../components/ModalDeleteConfirmation';
import GlobalContext from '../../../main/utils/GlobalContext';
import ModalAnonymiseUsers from '../../components/ModalAnonymiseUsers';
import { hasAccessAnonymiseUser } from '../../utils/access';

const ModalClassSettings = ({ classroomId, visible, onClose }) => {
  const { locales } = useContext(GlobalContext);
  const [lecturerOptions, setLecturerOptions] = useState([]);
  const [classroomName, setClassroomName] = useState(null);
  const [isCopied, setCopied] = useState(false);
  const [
    visibilityModalDeleteConfirmation,
    setVisibilityModalDeleteConfirmation,
  ] = useState(false);
  const [anonymiseVisible, setAnonymiseVisible] = useState(false);
  const [anonymiseLoading, setAnonymiseLoading] = useState(false);

  const {
    user,
    classroom: { current, uploading: classroomLoading },
    classroomLecturer: { collection, uploading },
    lecturers: { collection: lecturers, loading: lecturersLoading },
  } = useSelector((state) => state);

  const {
    classrooms: { deactivateClassroom, anonymiseUsers },
    classroom: { fetchClassroom, updateClassroomName },
    classroomLecturer: {
      fetchClassroomLecturers,
      addClassroomLecturer,
      removeClassroomLecturer,
    },
    lecturers: { fetchLecturers },
  } = useDispatch();

  useEffect(() => {
    if (classroomId) {
      fetchClassroom(classroomId);
      fetchClassroomLecturers(classroomId);
    }
  }, [classroomId]);

  useEffect(() => {
    if (current?.id === classroomId && current?.attributes?.establishment?.id) {
      fetchLecturers({
        establishmentId: current?.attributes?.establishment?.id,
      });
      setClassroomName(current?.name);
    }
  }, [current]);

  useEffect(() => {
    if (lecturers) {
      setLecturerOptions(
        lecturers.map((lecturer) => ({
          ...lecturer,
          ...lecturer.attributes,
          fullName: lecturer.attributes.name,
        })),
      );
    }
  }, [lecturers]);

  const handleOpenModalDeleteConfirmation = () =>
    setVisibilityModalDeleteConfirmation(true);

  const handleCloseModalDeleteConfirmation = () =>
    setVisibilityModalDeleteConfirmation(false);

  const handleSelect = (lecturerId) => {
    addClassroomLecturer({ classroomId, lecturerId });
  };

  const handleDelete = (lecturerId) => {
    removeClassroomLecturer({ classroomId, lecturerId });
  };

  const handleAnonymiseUsers = (confirmationPassword, groups) => {
    setAnonymiseLoading(true);
    anonymiseUsers({
      classroomId,
      confirmationPassword,
      groups,
      afterSuccess: () => {
        setAnonymiseVisible(false);
        onClose();
      },
      finish: () => setAnonymiseLoading(false),
    });
  };

  const handleDeactivateClassroom = () => {
    deactivateClassroom(classroomId);
    handleCloseModalDeleteConfirmation();
    onClose();
  };

  const handleChangeClassName = (event) => {
    setClassroomName(event.currentTarget.value);
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (classroomName !== null)
        updateClassroomName({ id: classroomId, name: classroomName });
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [classroomName]);

  return (
    <>
      <Modal
        isOpen={visible}
        onClose={onClose}
        title={locales.admin.classSettings}
      >
        {classroomLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 200,
            }}
          >
            <Spin size="large" />
          </div>
        ) : (
          <Form layout="vertical" className="mt-20">
            <div className="mb-20 mt-20">
              <Form.Item
                label={locales.admin.className}
                className="admin-input-label"
              >
                <Input
                  className="admin-input"
                  value={classroomName}
                  onChange={handleChangeClassName}
                />
              </Form.Item>
              <Form.Item
                label={locales.admin.shareLinkWithStudents}
                className="admin-input-label"
              >
                <Input
                  className="admin-input"
                  value={shareClassroomLink(current?.secretToken)}
                />
              </Form.Item>
              <div className="flex align-right">
                <Button
                  type="primary"
                  onClick={() => {
                    copyToClipboard(shareClassroomLink(current?.secretToken));
                    setCopied(true);
                  }}
                >
                  {isCopied ? locales.admin.copied : locales.admin.copyLink}
                </Button>
              </div>
            </div>

            <SelectUser
              avatarVisible
              users={lecturerOptions}
              loading={uploading || lecturersLoading}
              columns={[
                {
                  dataIndex: 'email',
                  render: (row) => {
                    return <CellFullName user={row} />;
                  },
                },
              ]}
              labelOfSelect={locales.admin.inviteOtherEducators}
              placeholderOfSelect={locales.admin.addEmailAddress}
              labelOfList={locales.admin.educators}
              selectedIds={collection.map((lecturer) => lecturer.id)}
              onSelect={handleSelect}
              onDelete={handleDelete}
            />

            <Divider />
            {/* Same destructive ghost buttons and spacing as the establishment modal */}
            <div className="flex space-between mt-20">
              <Space>
                <ActionButton
                  disabled={current?.attributes?.studentsCount > 0}
                  type="text"
                  onClick={handleOpenModalDeleteConfirmation}
                  danger
                  variant="destructiveGhost"
                >
                  {locales.common.deleteText}
                </ActionButton>
                {hasAccessAnonymiseUser(user) && (
                  <ActionButton
                    type="text"
                    onClick={() => setAnonymiseVisible(true)}
                    danger
                    variant="destructiveGhost"
                  >
                    {locales.admin.anonymiseAllUsers}
                  </ActionButton>
                )}
              </Space>
            </div>
          </Form>
        )}
        {/* Inside the settings modal so headlessui keeps focus in the nested dialog */}
        {hasAccessAnonymiseUser(user) && (
          <ModalAnonymiseUsers
            visible={anonymiseVisible}
            loading={anonymiseLoading}
            title={locales.admin.anonymiseClassroomUsersTitle}
            groups={[
              { key: 'students', label: locales.admin.users },
              { key: 'educators', label: locales.admin.educators },
            ]}
            onClose={() => setAnonymiseVisible(false)}
            onConfirm={handleAnonymiseUsers}
          />
        )}
      </Modal>
      <ModalDeleteConfirmation
        visible={visibilityModalDeleteConfirmation}
        confirmationTitle={locales.admin.confirmDeleteClassroom}
        onClose={handleCloseModalDeleteConfirmation}
        onDelete={handleDeactivateClassroom}
      />
    </>
  );
};

ModalClassSettings.propTypes = {
  classroomId: PropTypes.number,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
};
export default ModalClassSettings;
